import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X, Save,
  Clock, MapPin, User, Building, Phone, Mail, Trash2, Edit2,
  Target, Users, FileText, CheckCircle, AlertCircle, ArrowLeft, Download
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Task type configuration with colors
const TASK_TYPES = {
  follow_up: { label: 'Follow Up', labelEs: 'Seguimiento', color: '#f59e0b', icon: Phone },
  inspection: { label: 'Inspection', labelEs: 'Inspección', color: '#3b82f6', icon: CheckCircle },
  permit: { label: 'Permit Deadline', labelEs: 'Permiso', color: '#8b5cf6', icon: FileText },
  milestone: { label: 'Project Milestone', labelEs: 'Hito', color: '#10b981', icon: Target },
  payment: { label: 'Payment Due', labelEs: 'Pago', color: '#ef4444', icon: AlertCircle },
  custom: { label: 'Custom Task', labelEs: 'Tarea', color: '#6b7280', icon: CalendarIcon }
};

const TASK_STATUS = {
  pending: { label: 'Pending', labelEs: 'Pendiente', color: '#f59e0b' },
  in_progress: { label: 'In Progress', labelEs: 'En Progreso', color: '#3b82f6' },
  completed: { label: 'Completed', labelEs: 'Completado', color: '#10b981' },
  cancelled: { label: 'Cancelled', labelEs: 'Cancelado', color: '#6b7280' }
};

// Calendar helper functions
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const formatDate = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() && 
         d1.getMonth() === d2.getMonth() && 
         d1.getDate() === d2.getDate();
};

export default function Calendar() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // Refs
  const calendarRef = useRef(null);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [savingPdf, setSavingPdf] = useState(false);
  
  // Data state
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  
  // Task form state
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    task_type: 'custom',
    start_date: formatDate(new Date()),
    end_date: '',
    start_time: '09:00',
    end_time: '10:00',
    all_day: true,
    lead_id: '',
    client_id: '',
    status: 'pending',
    priority: 'medium',
    notes: ''
  });

  // Save to PDF function
  const saveCalendarToPdf = async () => {
    if (!calendarRef.current) return;
    
    setSavingPdf(true);
    toast.info(language === 'es' ? 'Generando PDF...' : 'Generating PDF...');
    
    try {
      const element = calendarRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const fileName = `calendar_${viewMode}_${currentDate.toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success(language === 'es' ? 'PDF guardado' : 'PDF saved');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
    } finally {
      setSavingPdf(false);
    }
  };

  // Fetch data
  useEffect(() => {
    fetchTasks();
    fetchLeadsAndClients();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks`);
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadsAndClients = async () => {
    try {
      const [leadsRes, clientsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/estimates`),
        fetch(`${BACKEND_URL}/api/clients`)
      ]);
      if (leadsRes.ok) setLeads(await leadsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      console.error('Error fetching leads/clients:', error);
    }
  };

  // Task CRUD operations
  const saveTask = async () => {
    try {
      const url = editingTask 
        ? `${BACKEND_URL}/api/tasks/${editingTask.id}`
        : `${BACKEND_URL}/api/tasks`;
      
      const method = editingTask ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm)
      });
      
      if (response.ok) {
        toast.success(editingTask 
          ? (language === 'es' ? 'Tarea actualizada' : 'Task updated')
          : (language === 'es' ? 'Tarea creada' : 'Task created')
        );
        fetchTasks();
        closeTaskModal();
      } else {
        toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta tarea?' : 'Delete this task?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Tarea eliminada' : 'Task deleted');
        fetchTasks();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const updateTaskDate = async (taskId, newDate) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: formatDate(newDate) })
      });
      
      if (response.ok) {
        toast.success(language === 'es' ? 'Tarea movida' : 'Task moved');
        fetchTasks();
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // Modal handlers
  const openNewTaskModal = (date = selectedDate) => {
    setEditingTask(null);
    setTaskForm({
      title: '',
      description: '',
      task_type: 'custom',
      start_date: formatDate(date),
      end_date: '',
      start_time: '09:00',
      end_time: '10:00',
      all_day: true,
      lead_id: '',
      client_id: '',
      status: 'pending',
      priority: 'medium',
      notes: ''
    });
    setShowTaskModal(true);
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      task_type: task.task_type || 'custom',
      start_date: task.start_date || formatDate(new Date()),
      end_date: task.end_date || '',
      start_time: task.start_time || '09:00',
      end_time: task.end_time || '10:00',
      all_day: task.all_day !== false,
      lead_id: task.lead_id || '',
      client_id: task.client_id || '',
      status: task.status || 'pending',
      priority: task.priority || 'medium',
      notes: task.notes || ''
    });
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setEditingTask(null);
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, date) => {
    e.preventDefault();
    if (draggedTask) {
      updateTaskDate(draggedTask.id, date);
      setDraggedTask(null);
    }
  };

  // Navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    return tasks.filter(task => isSameDay(task.start_date, date));
  };

  // Calendar data
  const monthData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Previous month days
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(nextYear, nextMonth, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [currentDate]);

  // Week data
  const weekData = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentDate]);

  const dayNames = language === 'es' 
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthNames = language === 'es'
    ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const isToday = (date) => isSameDay(date, new Date());
  const isSelected = (date) => isSameDay(date, selectedDate);

  // Render task pill
  const renderTaskPill = (task, compact = false) => {
    const taskType = TASK_TYPES[task.task_type] || TASK_TYPES.custom;
    const TaskIcon = taskType.icon;
    
    return (
      <div
        key={task.id}
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onClick={(e) => { e.stopPropagation(); openEditTaskModal(task); }}
        className={`group cursor-pointer rounded px-2 py-1 text-white text-xs truncate flex items-center gap-1 hover:opacity-90 transition-opacity ${compact ? 'mb-0.5' : 'mb-1'}`}
        style={{ backgroundColor: task.color || taskType.color }}
        title={task.title}
      >
        <TaskIcon className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">{task.title}</span>
        {task.status === 'completed' && <CheckCircle className="h-3 w-3 flex-shrink-0 ml-auto" />}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <nav className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin" className="flex items-center text-gray-600 hover:text-gray-900">
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">{language === 'es' ? 'Volver' : 'Back'}</span>
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <Link to="/" className="hover:opacity-80">
                <CompanyLogo className="h-8 w-auto" />
              </Link>
            </div>
            
            <div className="flex items-center space-x-2">
              <h1 className="text-lg font-bold text-gray-800 flex items-center">
                <CalendarIcon className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                {language === 'es' ? 'Calendario' : 'Calendar'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <LanguageToggle />
              <Button 
                onClick={saveCalendarToPdf}
                variant="outline"
                disabled={savingPdf}
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
              >
                <Download className="h-4 w-4 mr-2" />
                {savingPdf ? '...' : (language === 'es' ? 'Guardar PDF' : 'Save PDF')}
              </Button>
              <Button 
                onClick={() => openNewTaskModal()}
                style={{ backgroundColor: COMPANY.primaryColor }}
                className="text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Nueva Tarea' : 'New Task'}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {/* Calendar Controls */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToToday}>
                  {language === 'es' ? 'Hoy' : 'Today'}
                </Button>
                <div className="flex items-center">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => viewMode === 'month' ? navigateMonth(-1) : viewMode === 'week' ? navigateWeek(-1) : navigateDay(-1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => viewMode === 'month' ? navigateMonth(1) : viewMode === 'week' ? navigateWeek(1) : navigateDay(1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <h2 className="text-xl font-semibold min-w-[200px] text-center">
                  {viewMode === 'day' 
                    ? currentDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                    : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                  }
                </h2>
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {['month', 'week', 'day'].map((mode) => (
                  <Button
                    key={mode}
                    variant={viewMode === mode ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode(mode)}
                    className={viewMode === mode ? 'text-white' : ''}
                    style={viewMode === mode ? { backgroundColor: COMPANY.primaryColor } : {}}
                  >
                    {language === 'es' 
                      ? (mode === 'month' ? 'Mes' : mode === 'week' ? 'Semana' : 'Día')
                      : (mode === 'month' ? 'Month' : mode === 'week' ? 'Week' : 'Day')
                    }
                  </Button>
                ))}
              </div>
              
              {/* Task Type Legend */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(TASK_TYPES).map(([key, type]) => (
                  <div key={key} className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
                    <span className="text-gray-600">{language === 'es' ? type.labelEs : type.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        <Card ref={calendarRef}>
          <CardContent className="p-0">
            {/* Month View */}
            {viewMode === 'month' && (
              <div>
                {/* Day Headers */}
                <div className="grid grid-cols-7 border-b">
                  {dayNames.map((day) => (
                    <div key={day} className="p-2 text-center font-semibold text-sm text-gray-600 bg-gray-50">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Days */}
                <div className="grid grid-cols-7">
                  {monthData.map((dayData, index) => {
                    const dayTasks = getTasksForDate(dayData.date);
                    return (
                      <div
                        key={index}
                        className={`min-h-[120px] border-b border-r p-1 cursor-pointer transition-colors
                          ${!dayData.isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''}
                          ${isToday(dayData.date) ? 'bg-amber-50' : ''}
                          ${isSelected(dayData.date) ? 'ring-2 ring-inset ring-amber-400' : ''}
                          hover:bg-gray-100
                        `}
                        onClick={() => { setSelectedDate(dayData.date); openNewTaskModal(dayData.date); }}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, dayData.date)}
                      >
                        <div className={`text-sm font-medium mb-1 ${isToday(dayData.date) ? 'text-amber-600' : ''}`}>
                          {dayData.date.getDate()}
                        </div>
                        <div className="space-y-0.5 overflow-hidden max-h-[90px]">
                          {dayTasks.slice(0, 3).map(task => renderTaskPill(task, true))}
                          {dayTasks.length > 3 && (
                            <div className="text-xs text-gray-500 pl-2">
                              +{dayTasks.length - 3} {language === 'es' ? 'más' : 'more'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Week View */}
            {viewMode === 'week' && (
              <div>
                {/* Day Headers */}
                <div className="grid grid-cols-7 border-b">
                  {weekData.map((date, index) => (
                    <div 
                      key={index} 
                      className={`p-2 text-center border-r ${isToday(date) ? 'bg-amber-50' : 'bg-gray-50'}`}
                    >
                      <div className="text-xs text-gray-500">{dayNames[date.getDay()]}</div>
                      <div className={`text-lg font-semibold ${isToday(date) ? 'text-amber-600' : ''}`}>
                        {date.getDate()}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Week Days */}
                <div className="grid grid-cols-7 min-h-[500px]">
                  {weekData.map((date, index) => {
                    const dayTasks = getTasksForDate(date);
                    return (
                      <div
                        key={index}
                        className={`border-r p-2 cursor-pointer hover:bg-gray-50
                          ${isToday(date) ? 'bg-amber-50/50' : ''}
                          ${isSelected(date) ? 'ring-2 ring-inset ring-amber-400' : ''}
                        `}
                        onClick={() => { setSelectedDate(date); openNewTaskModal(date); }}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, date)}
                      >
                        {dayTasks.map(task => renderTaskPill(task))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Day View */}
            {viewMode === 'day' && (
              <div className="p-4">
                <div 
                  className="min-h-[500px] bg-gray-50 rounded-lg p-4"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, currentDate)}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                      {language === 'es' ? 'Tareas del día' : "Today's Tasks"}
                    </h3>
                    <Badge variant="outline">{getTasksForDate(currentDate).length} {language === 'es' ? 'tareas' : 'tasks'}</Badge>
                  </div>
                  
                  {getTasksForDate(currentDate).length === 0 ? (
                    <div className="text-center text-gray-500 py-12">
                      <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>{language === 'es' ? 'No hay tareas para este día' : 'No tasks for this day'}</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => openNewTaskModal(currentDate)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Agregar Tarea' : 'Add Task'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getTasksForDate(currentDate).map(task => {
                        const taskType = TASK_TYPES[task.task_type] || TASK_TYPES.custom;
                        const TaskIcon = taskType.icon;
                        const statusInfo = TASK_STATUS[task.status] || TASK_STATUS.pending;
                        
                        return (
                          <Card 
                            key={task.id} 
                            className="cursor-pointer hover:shadow-md transition-shadow"
                            draggable
                            onDragStart={(e) => handleDragStart(e, task)}
                            onClick={() => openEditTaskModal(task)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div 
                                  className="p-2 rounded-lg"
                                  style={{ backgroundColor: `${task.color || taskType.color}20` }}
                                >
                                  <TaskIcon className="h-5 w-5" style={{ color: task.color || taskType.color }} />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold">{task.title}</h4>
                                    <Badge 
                                      variant="outline" 
                                      style={{ borderColor: statusInfo.color, color: statusInfo.color }}
                                    >
                                      {language === 'es' ? statusInfo.labelEs : statusInfo.label}
                                    </Badge>
                                  </div>
                                  {task.description && (
                                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                    {!task.all_day && task.start_time && (
                                      <span className="flex items-center">
                                        <Clock className="h-3 w-3 mr-1" />
                                        {task.start_time} - {task.end_time || ''}
                                      </span>
                                    )}
                                    {task.lead_name && (
                                      <span className="flex items-center">
                                        <Target className="h-3 w-3 mr-1" />
                                        {task.lead_name}
                                      </span>
                                    )}
                                    {task.client_name && (
                                      <span className="flex items-center">
                                        <Users className="h-3 w-3 mr-1" />
                                        {task.client_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingTask 
                    ? (language === 'es' ? 'Editar Tarea' : 'Edit Task')
                    : (language === 'es' ? 'Nueva Tarea' : 'New Task')
                  }
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={closeTaskModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div>
                <Label>{language === 'es' ? 'Título' : 'Title'} *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder={language === 'es' ? 'Título de la tarea' : 'Task title'}
                  className="mt-1"
                />
              </div>
              
              {/* Task Type */}
              <div>
                <Label>{language === 'es' ? 'Tipo de Tarea' : 'Task Type'}</Label>
                <Select value={taskForm.task_type} onValueChange={(v) => setTaskForm({ ...taskForm, task_type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_TYPES).map(([key, type]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: type.color }} />
                          {language === 'es' ? type.labelEs : type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'es' ? 'Fecha Inicio' : 'Start Date'} *</Label>
                  <Input
                    type="date"
                    value={taskForm.start_date}
                    onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Fecha Fin' : 'End Date'}</Label>
                  <Input
                    type="date"
                    value={taskForm.end_date}
                    onChange={(e) => setTaskForm({ ...taskForm, end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* All Day Toggle */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="all_day"
                  checked={taskForm.all_day}
                  onCheckedChange={(checked) => setTaskForm({ ...taskForm, all_day: checked })}
                />
                <Label htmlFor="all_day">{language === 'es' ? 'Todo el día' : 'All day'}</Label>
              </div>
              
              {/* Time (if not all day) */}
              {!taskForm.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{language === 'es' ? 'Hora Inicio' : 'Start Time'}</Label>
                    <Input
                      type="time"
                      value={taskForm.start_time}
                      onChange={(e) => setTaskForm({ ...taskForm, start_time: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{language === 'es' ? 'Hora Fin' : 'End Time'}</Label>
                    <Input
                      type="time"
                      value={taskForm.end_time}
                      onChange={(e) => setTaskForm({ ...taskForm, end_time: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
              
              {/* Link to Lead or Client */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'es' ? 'Vincular a Lead' : 'Link to Lead'}</Label>
                  <Select 
                    value={taskForm.lead_id || 'none'} 
                    onValueChange={(v) => setTaskForm({ ...taskForm, lead_id: v === 'none' ? '' : v, client_id: '' })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{language === 'es' ? 'Ninguno' : 'None'}</SelectItem>
                      {leads.map((lead) => (
                        <SelectItem key={lead.id} value={lead.id}>
                          {lead.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'es' ? 'Vincular a Cliente' : 'Link to Client'}</Label>
                  <Select 
                    value={taskForm.client_id || 'none'} 
                    onValueChange={(v) => setTaskForm({ ...taskForm, client_id: v === 'none' ? '' : v, lead_id: '' })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{language === 'es' ? 'Ninguno' : 'None'}</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Status */}
              <div>
                <Label>{language === 'es' ? 'Estado' : 'Status'}</Label>
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_STATUS).map(([key, status]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                          {language === 'es' ? status.labelEs : status.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Description */}
              <div>
                <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder={language === 'es' ? 'Descripción de la tarea...' : 'Task description...'}
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              {/* Notes */}
              <div>
                <Label>{language === 'es' ? 'Notas' : 'Notes'}</Label>
                <Textarea
                  value={taskForm.notes}
                  onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
                  placeholder={language === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                {editingTask && (
                  <Button variant="destructive" onClick={() => { deleteTask(editingTask.id); closeTaskModal(); }}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Eliminar' : 'Delete'}
                  </Button>
                )}
                <div className={`flex gap-2 ${editingTask ? '' : 'ml-auto'}`}>
                  <Button variant="outline" onClick={closeTaskModal}>
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={saveTask}
                    disabled={!taskForm.title || !taskForm.start_date}
                    style={{ backgroundColor: COMPANY.primaryColor }}
                    className="text-white"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Guardar' : 'Save'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
